// src/pages/form/FormBuilderPage.tsx
// Form builder — one page, four tabs (?tab=), mirrors CalendarSettingsPage.
// Builder tab uses the WIP form-kit (FormRow/InlineEditText/SegmentedControl)
// for field editing. Replaces the generated Detail/Edit scaffold pages.
// See docs/ux/form-ia.md.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert, Badge } from '@react/components';
import { InlineEditText } from '@/components/form';
import { formApi } from '@core/api';
import type { Form } from '@core/types/form';
import { BuilderTab } from '@/features/form/BuilderTab';
import { SubmissionsTab } from '@/features/form/SubmissionsTab';
import { AnalyticsTab } from '@/features/form/AnalyticsTab';
import { SettingsTab } from '@/features/form/SettingsTab';
import { errMessage, formStatusBadgeVariant } from '@/features/form/helpers';

type Tab = 'builder' | 'submissions' | 'analytics' | 'settings';
const TABS: Tab[] = ['builder', 'submissions', 'analytics', 'settings'];

export const FormBuilderPage = observer(() => {
  const { t } = useI18n();
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [form, setForm] = useState<Form | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'builder';
  }, [searchParams]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    formApi
      .GetForm(id)
      .then((res) => setForm(res.form ?? null))
      .catch((e) => setError(errMessage(e, t('form.builder.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renameForm = async (name: string) => {
    if (!form?.entity?.id) return;
    try {
      const res = await formApi.UpdateForm(form.entity.id, { form: { ...form, name } });
      if (res.form) setForm(res.form);
    } catch (e) {
      setError(errMessage(e, t('form.builder.saveError')));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3 p-6" aria-busy="true">
        <div className="h-8 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="p-6">
        <Alert variant="error">{error}</Alert>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">{t('form.builder.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <InlineEditText
            value={form.name}
            onCommit={renameForm}
            variant="title"
            aria-label={t('form.builder.namePlaceholder')}
          />
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={formStatusBadgeVariant(form.status)}>
              {t(`form.status.${(form.status ?? 'FORM_STATUS_DRAFT').replace('FORM_STATUS_', '').toLowerCase()}`)}
            </Badge>
          </div>
        </div>
        <Link to="/forms" className="shrink-0 text-sm text-primary hover:underline">
          {t('form.builder.backToList')}
        </Link>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div role="tablist" aria-label={t('form.builder.tabsLabel')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`form.builder.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'builder' && <BuilderTab form={form} onFormChanged={setForm} />}
      {tab === 'submissions' && <SubmissionsTab form={form} />}
      {tab === 'analytics' && <AnalyticsTab form={form} />}
      {tab === 'settings' && <SettingsTab form={form} onFormChanged={setForm} />}
    </div>
  );
});
